import { useSignedInIdentity } from "../../../../shared/useIdentity";
import { useCoreNotifications } from "../../../../shared/useCoreNotifications";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import academyLogo from "../../../../shared/brand/chatoner-ai-academy-logo-landscape.png";
import academyMark from "../../../../shared/brand/chatoner-ai-academy-mark.png";
import { AppModal } from "../../components/proctor/AppModal";
import {
  Button,
  DifferentiatedWorkspacePage,
  EmptyState,
  Icon,
  WorkspaceRouteBoundary,
  type AppCallbacks,
} from "../../components/proctor/WorkspaceLayouts";
import { platformEnvironment } from "../../config/environment";
import { APP_DATA, PROCTOR_ROUTE_SPECS } from "../../data/mockData";
import { requestExternalHandoff } from "../../services/externalHandoff";
import { academyVersionRequest } from "../../../../shared/academyApi";
import {
  AccommodationsPage,
  ClassPage,
  CommandPage,
  CommunicationsPage,
  EvidencePage,
  GovernancePage,
  IncidentsPage,
  LabsPage,
  ReadinessPage,
  ReportsPage,
  RoomPage,
} from "../../pages/operationalPages";
import { CSS, dropdownThemeStyles } from "../../styles/theme";
import type {
  AreaDef,
  Candidate,
  EvidenceRecord,
  Incident,
  ModalKind,
  ModalState,
  Preferences,
  ProctorWorkspaceSpec,
  RouteDef,
  Session,
  Toast,
} from "../../types/domain";
import { safeRead, safeWrite } from "../../utils/browser";

const findArea = (id: string) => APP_DATA.areas.find((a: AreaDef) => a.id === id) || APP_DATA.areas[0];
const findRoute = (area: AreaDef, id: string) =>
  area.routes.find((r: RouteDef) => r.id === id) || area.routes[0];
const parseLocation = () => {
  const raw = (typeof window !== "undefined" ? window.location.hash : "")
    .replace(/^#\/?/, "")
    .split("/")
    .filter(Boolean);
  const area = findArea(raw[0] || "command");
  const route = findRoute(area, raw[1] || area.routes[0].id);
  return { areaId: area.id, pageId: route.id };
};
const languageCode = (name: string) =>
  (
    ({
      English: "EN",
      Luganda: "LG",
      Kiswahili: "SW",
      French: "FR",
      Arabic: "AR",
      Portuguese: "PT",
      Spanish: "ES",
      Hindi: "HI",
      None: "—",
    }) as Record<string, string>
  )[name] || name.slice(0, 2).toUpperCase();

const D16_UNCERTIFIED_PROCTOR_ANALYTICS = new Set([
  "incident-analytics",
  "technical-reliability",
  "accommodation-delivery",
  "proctor-performance",
]);

function App() {
  const identity = useSignedInIdentity();
  const initial = useMemo(parseLocation, []);
  const [areaId, setAreaId] = useState(initial.areaId);
  const [pageId, setPageId] = useState(initial.pageId);
  const [mobileNav, setMobileNav] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [modal, setModal] = useState<ModalState>({ kind: null });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [classSessions, setClassSessions] = useState<Session[]>([]);
  const [classSessionsLoading, setClassSessionsLoading] = useState(true);
  const [classSessionsError, setClassSessionsError] = useState("");
  const [prefs, setPrefs] = useState<Preferences>(() =>
    safeRead("chatoner-proctor:prefs", {
      primaryLanguage: "English",
      secondaryLanguage: "Luganda",
      translations: true,
      captions: true,
      reducedMotion: false,
      highContrast: false,
      compact: false,
      signalSounds: true,
      desktopNotifications: true,
      autoOpenUrgent: true,
    }),
  );
  const coreInbox = useCoreNotifications();
  const notifications = coreInbox.notifications.map(item => ({
    id: item.id,
    title: item.title,
    message: item.body,
    time: new Date(item.createdAt).toLocaleString(),
    read: Boolean(item.readAt),
    deepLink: item.deepLink,
    area: "notifications",
    page: "notifications",
  }));
  const searchRef = useRef<HTMLInputElement | null>(null);
  const currentArea = findArea(areaId);
  const currentRoute = findRoute(currentArea, pageId);
  const unread = coreInbox.unreadCount;

  const toast = (title: string, message: string, tone: Toast["tone"] = "info") => {
    const id = Date.now() + Math.floor(Math.random() * 999);
    setToasts(v => [...v, { id, title, message, tone }]);
    setTimeout(() => setToasts(v => v.filter(t => t.id !== id)), 4600);
  };
  const openModal = (kind: ModalKind, payload?: unknown) => setModal({ kind, payload });
  const closeModal = () => setModal({ kind: null });
  const navigate = useCallback(
    (nextArea: string, nextPage: string) => {
      const area = findArea(nextArea);
      const route = findRoute(area, nextPage);
      setAreaId(area.id);
      setPageId(route.id);
      setMobileNav(false);
      setSearchOpen(false);
      window.location.hash = `#/${area.id}/${route.id}`;
      window.scrollTo({ top: 0, behavior: prefs.reducedMotion ? "auto" : "smooth" });
    },
    [prefs.reducedMotion],
  );
  const openExternal = (url: string, label: string) => {
    try {
      const handoff = requestExternalHandoff(url);
      toast(
        handoff.opened ? `${label} opened` : `${label} demonstration handoff`,
        handoff.opened
          ? "The independent platform opened in a new tab."
          : "The external platform is not connected; no data was sent.",
        handoff.opened ? "good" : "info",
      );
    } catch {
      toast(`Open ${label}`, url, "info");
    }
  };
  const openClass = (session: Session, mode = "proctor") => {
    openModal("class-handoff", { session, mode });
  };
  const openLab = (session: Session, destination = "proctor-home") => {
    openModal("lab-handoff", { session, destination });
  };
  const cb: AppCallbacks = {
    navigate,
    toast,
    openModal,
    openClass,
    openLab,
    openExternal,
    classSessions,
    classSessionsLoading,
    classSessionsError,
  };

  useEffect(() => {
    let active = true;
    void academyVersionRequest<
      Array<{
        exam_session_id: string;
        assessment_title: string;
        cohort_name: string;
        starts_at: string;
        ends_at: string;
        platform: string;
        state: string;
        assignment_role: string;
      }>
    >("v1/proctor/sessions?limit=200")
      .then(items => {
        if (!active) return;
        setClassSessions(
          items
            .filter(item => item.platform.toLowerCase().includes("class"))
            .map(item => ({
              id: item.exam_session_id,
              title: item.assessment_title,
              program: item.cohort_name,
              date: new Date(item.starts_at).toLocaleDateString(),
              time: `${new Date(item.starts_at).toLocaleTimeString()}–${new Date(item.ends_at).toLocaleTimeString()}`,
              duration: "",
              platform: "Chatoner Class",
              code: "One-time handoff",
              location: "Chatoner Class",
              status: item.state,
              candidates: 0,
              ready: 0,
              lead: "Assigned team",
              role: item.assignment_role,
              risk: "Published exam policy",
            })),
        );
        setClassSessionsError("");
      })
      .catch(cause => {
        if (active)
          setClassSessionsError(
            cause instanceof Error ? cause.message : "Assigned Class sessions unavailable",
          );
      })
      .finally(() => {
        if (active) setClassSessionsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    safeWrite("chatoner-proctor:prefs", prefs);
    document.documentElement.classList.toggle("high-contrast", prefs.highContrast);
    document.documentElement.classList.toggle("compact", prefs.compact);
  }, [prefs]);
  useEffect(() => {
    const onHash = () => {
      const next = parseLocation();
      setAreaId(next.areaId);
      setPageId(next.pageId);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchRef.current?.focus(), 0);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
        closeModal();
        setMobileNav(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    window.__CHATONER_PROCTOR_CONTEXT__ = {
      role: "exam-proctor",
      area: areaId,
      page: pageId,
      primary_language: prefs.primaryLanguage,
    };
    window.__CHATONER_PROCTOR_NAVIGATE__ = navigate;
    window.__CHATONER_PROCTOR_PLATFORM__ = {
      version: "2.0.0",
      routeCount: Object.keys(PROCTOR_ROUTE_SPECS).length,
      modelCount: new Set(Object.values(PROCTOR_ROUTE_SPECS).map(spec => spec.model)).size,
      familyCount: new Set(Object.values(PROCTOR_ROUTE_SPECS).map(spec => spec.family)).size,
      area: areaId,
      page: pageId,
    };
  }, [areaId, navigate, pageId, prefs.primaryLanguage]);

  const grouped = useMemo(() => {
    const groups: Record<string, RouteDef[]> = {};
    currentArea.routes.forEach((route: RouteDef) => {
      (groups[route.group] || (groups[route.group] = [])).push(route);
    });
    return Object.entries(groups);
  }, [currentArea]);
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [] as any[];
    const routes = APP_DATA.areas
      .flatMap((a: AreaDef) =>
        a.routes.map((r: RouteDef) => ({
          kind: "route",
          title: r.title,
          subtitle: `${a.label} · ${r.description}`,
          icon: r.icon,
          area: a.id,
          page: r.id,
        })),
      )
      .filter((v: any) => `${v.title} ${v.subtitle}`.toLowerCase().includes(q));
    const candidates = APP_DATA.candidates
      .filter((c: Candidate) =>
        `${c.name} ${c.candidateNo} ${c.program} ${c.status}`.toLowerCase().includes(q),
      )
      .slice(0, 8)
      .map((c: Candidate) => ({
        kind: "candidate",
        title: c.name,
        subtitle: `${c.candidateNo} · ${c.status} · ${c.program}`,
        icon: "user",
        payload: c,
      }));
    const sessions = APP_DATA.sessions
      .filter((s: Session) => `${s.title} ${s.code} ${s.platform} ${s.program}`.toLowerCase().includes(q))
      .map((s: Session) => ({
        kind: "session",
        title: s.title,
        subtitle: `${s.code} · ${s.platform} · ${s.date}`,
        icon: s.platform.includes("Labs") ? "flask" : s.platform.includes("Class") ? "video" : "shield",
        payload: s,
      }));
    const incidents = APP_DATA.incidents
      .filter((i: Incident) => `${i.id} ${i.candidate} ${i.summary} ${i.category}`.toLowerCase().includes(q))
      .slice(0, 6)
      .map((i: Incident) => ({
        kind: "incident",
        title: i.summary,
        subtitle: `${i.id} · ${i.candidate} · ${i.status}`,
        icon: "alert",
        payload: i,
      }));
    const evidence = APP_DATA.evidence
      .filter((e: EvidenceRecord) => `${e.id} ${e.type} ${e.candidate}`.toLowerCase().includes(q))
      .slice(0, 6)
      .map((e: EvidenceRecord) => ({
        kind: "evidence",
        title: e.type,
        subtitle: `${e.id} · ${e.candidate} · ${e.status}`,
        icon: "database",
        payload: e,
      }));
    return [...routes.slice(0, 15), ...sessions, ...candidates, ...incidents, ...evidence].slice(0, 30);
  }, [searchQuery]);
  const openSearchResult = (r: any) => {
    if (r.kind === "route") navigate(r.area, r.page);
    else if (r.kind === "candidate") openModal("candidate-detail", r.payload);
    else if (r.kind === "session") openModal("session-detail", r.payload);
    else if (r.kind === "incident") openModal("incident-detail", r.payload);
    else if (r.kind === "evidence") openModal("evidence-detail", r.payload);
    setSearchOpen(false);
    setSearchQuery("");
  };

  const routeSpec = (PROCTOR_ROUTE_SPECS as Record<string, ProctorWorkspaceSpec>)[pageId];
  if (!routeSpec) throw new Error(`Unregistered Exam Proctor route: ${areaId}/${pageId}`);
  let page: React.ReactNode;
  if (D16_UNCERTIFIED_PROCTOR_ANALYTICS.has(pageId))
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <div data-d16-analytics-state="unavailable">
          <EmptyState
            icon="chart"
            title={`${routeSpec.title} unavailable`}
            text="No sample metrics or exports are shown. Academy Analytics will enable this route after its owner source, purpose, suppression and freshness contract is certified."
          />
        </div>
      </WorkspaceRouteBoundary>
    );
  else if (pageId === "live-control-room")
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <CommandPage cb={cb} />
      </WorkspaceRouteBoundary>
    );
  else if (
    [
      "readiness-dashboard",
      "candidate-roster",
      "identity-verification",
      "device-network-checks",
      "room-environment-checks",
      "late-reentry-queue",
    ].includes(pageId)
  )
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <ReadinessPage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (
    [
      "active-exam-room",
      "secure-messages",
      "session-controls",
      "breaks-reentry",
      "emergency-procedures",
    ].includes(pageId)
  )
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <RoomPage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (pageId === "class-connection")
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <ClassPage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (pageId === "labs-connection")
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <LabsPage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (pageId === "incident-queue")
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <IncidentsPage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (pageId === "evidence-records")
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <EvidencePage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (pageId === "approved-accommodations")
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <AccommodationsPage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (pageId === "secure-communications")
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <CommunicationsPage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (pageId === "session-reports")
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <ReportsPage cb={cb} route={currentRoute} />
      </WorkspaceRouteBoundary>
    );
  else if (["authority-boundaries", "language-accessibility"].includes(pageId))
    page = (
      <WorkspaceRouteBoundary spec={routeSpec}>
        <GovernancePage cb={cb} route={currentRoute} prefs={prefs} setPrefs={setPrefs} />
      </WorkspaceRouteBoundary>
    );
  else page = <DifferentiatedWorkspacePage cb={cb} spec={routeSpec} />;

  return (
    <div className={`proctor-app ${mobileNav ? "mobile-nav-open" : ""}`} data-platform-version="2.0.0">
      <div className="mobile-overlay" onClick={() => setMobileNav(false)} />
      <div className="app-shell">
        <aside className="app-rail" aria-label="Exam Proctor application areas">
          <button
            type="button"
            className="brand-mark"
            onClick={() => navigate("command", "live-control-room")}
            aria-label="Chatoner AI Academy Proctor home"
            title="Proctor home"
          >
            <img
              className="academy-platform-mark"
              src={academyMark}
              alt=""
              width={837}
              height={765}
              decoding="async"
            />
            <span className="rail-tip">Proctor home</span>
          </button>
          <nav className="rail-nav">
            {APP_DATA.areas.map((area: AreaDef) => (
              <button
                type="button"
                key={area.id}
                title={area.label}
                aria-label={area.label}
                className={`rail-button ${areaId === area.id ? "active" : ""}`}
                onClick={() => navigate(area.id, area.routes[0].id)}
                data-testid={`rail-${area.id}`}
              >
                <Icon name={area.icon} />
                {area.routes.reduce((n: number, r: RouteDef) => n + Number(r.badge || 0), 0) > 0 &&
                  ["command", "incidents"].includes(area.id) && (
                    <span className="rail-badge">{area.id === "command" ? "8" : "9"}</span>
                  )}
                <span className="rail-tip">{area.label}</span>
              </button>
            ))}
          </nav>
          <div className="rail-spacer" />
          <button
            type="button"
            className="rail-button"
            title="Help"
            aria-label="Help"
            onClick={() => openModal("settings-help")}
          >
            <Icon name="help" />
            <span className="rail-tip">Help</span>
          </button>
          <button
            type="button"
            className="rail-profile"
            onClick={() => openModal("profile")}
            aria-label="Open Exam Proctor profile"
            title="Exam Proctor profile"
          >
            {identity.initials}
            <span className="rail-tip">Exam Proctor profile</span>
          </button>
        </aside>
        <aside className="context-sidebar" aria-label={`${currentArea.label} navigation`}>
          <div className="academy-platform-sidebar-brand">
            <img
              className="academy-platform-logo"
              src={academyLogo}
              alt="Chatoner AI Academy"
              width={4124}
              height={967}
              decoding="async"
            />
          </div>
          <div className="sidebar-top">
            <button type="button" className="workspace-switch" onClick={() => openModal("profile")}>
              <span>
                <Icon name="shield" />
              </span>
              <span className="workspace-copy">
                <b>Exam Proctor Studio</b>
                <small>
                  {identity.name} · {identity.email}
                </small>
              </span>
              <Icon name="chevron" size={14} />
            </button>
          </div>
          <div className="sidebar-context">
            <small>Assessment integrity</small>
            <h1>{currentArea.label}</h1>
            <p>{currentArea.routes[0]?.description}</p>
          </div>
          <div className="sidebar-scroll">
            {grouped.map(([group, routes]) => (
              <div className="sidebar-group" key={group}>
                <button
                  type="button"
                  className={`sidebar-group-head ${collapsed[group] ? "collapsed" : ""}`}
                  onClick={() => setCollapsed(v => ({ ...v, [group]: !v[group] }))}
                >
                  <span>{group}</span>
                  <Icon name="chevron" size={12} />
                </button>
                {!collapsed[group] && (
                  <div className="sidebar-list">
                    {routes.map((route: RouteDef) => (
                      <button
                        type="button"
                        key={route.id}
                        className={`sidebar-link ${pageId === route.id ? "active" : ""}`}
                        onClick={() => navigate(currentArea.id, route.id)}
                        data-testid={`route-${route.id}`}
                      >
                        <Icon name={route.icon} size={15} />
                        <span>{route.title}</span>
                        {route.badge && <span className="badge">{route.badge}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="sidebar-footer">
            <div className="readiness-card">
              <b>
                <Icon name="check" size={14} />
                Proctor readiness current
              </b>
              <p>84 differentiated destinations · 41 operational models · role boundaries current.</p>
              <Button small icon="arrow" onClick={() => navigate("governance", "proctor-certification")}>
                Open readiness
              </Button>
            </div>
          </div>
        </aside>
        <main className="main-shell">
          <header className="topbar">
            <button
              type="button"
              className="top-icon mobile-menu"
              onClick={() => setMobileNav(true)}
              aria-label="Open navigation"
              data-testid="mobile-menu"
            >
              <Icon name="menu" />
            </button>
            <div className="breadcrumbs">
              <small>{currentArea.label}</small>
              <strong>{currentRoute.title}</strong>
            </div>
            <div className="topbar-spacer" />
            <button
              type="button"
              className="global-search"
              onClick={() => {
                setSearchOpen(true);
                setTimeout(() => searchRef.current?.focus(), 0);
              }}
              aria-label="Search Exam Proctor workspace"
            >
              <Icon name="search" size={15} />
              <span>Search sessions, candidates, incidents, evidence…</span>
              <kbd className="key-hint">Ctrl K</kbd>
            </button>
            <button
              type="button"
              className="language-button"
              onClick={() => openModal("language")}
              aria-label="Language and translation"
            >
              <Icon name="globe" size={15} />
              <span>
                {languageCode(prefs.primaryLanguage)} + {languageCode(prefs.secondaryLanguage)}
              </span>
            </button>
            <button
              type="button"
              className="top-icon"
              onClick={() =>
                openModal("notifications", {
                  notifications,
                  onRead: (id: string) => coreInbox.markRead(id),
                  onReadAll: () => coreInbox.markAllRead(),
                  openLink: (deepLink: string) => {
                    const access = new URL(platformEnvironment.appOrigins.auth);
                    const prefix = access.pathname.replace(/\/auth\/?$/, "");
                    if (deepLink.startsWith("/")) window.location.assign(access.origin + prefix + deepLink);
                  },
                })
              }
              aria-label={`Notifications, ${unread} unread`}
            >
              <Icon name="bell" />
              {unread > 0 && <i className="dot" />}
            </button>
            <button
              type="button"
              className="top-icon"
              onClick={() => openModal("settings-help", { title: currentRoute.title })}
              aria-label="Proctor help"
            >
              <Icon name="help" />
            </button>
          </header>
          <div className="content" key={`${areaId}-${pageId}`}>
            {page}
          </div>
        </main>
      </div>
      <nav className="mobile-bottom" aria-label="Mobile Exam Proctor navigation">
        <button
          type="button"
          className={areaId === "command" ? "active" : ""}
          onClick={() => navigate("command", "live-control-room")}
        >
          <Icon name="home" />
          <span>Command</span>
        </button>
        <button
          type="button"
          className={areaId === "sessions" ? "active" : ""}
          onClick={() => navigate("sessions", "scheduled-sessions")}
        >
          <Icon name="calendar" />
          <span>Sessions</span>
        </button>
        <button
          type="button"
          className={areaId === "rooms" ? "active" : ""}
          onClick={() => navigate("rooms", "active-exam-room")}
        >
          <Icon name="monitor" />
          <span>Live room</span>
        </button>
        <button
          type="button"
          className={areaId === "incidents" ? "active" : ""}
          onClick={() => navigate("incidents", "incident-queue")}
        >
          <Icon name="alert" />
          <span>Incidents</span>
        </button>
        <button type="button" onClick={() => setMobileNav(true)}>
          <Icon name="grid" />
          <span>More</span>
        </button>
      </nav>
      {searchOpen && (
        <div
          className="search-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Search Exam Proctor workspace"
          onMouseDown={e => {
            if (e.currentTarget === e.target) setSearchOpen(false);
          }}
        >
          <section className="search-panel">
            <div className="search-input-row">
              <Icon name="search" />
              <input
                ref={searchRef}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search all proctor workspaces…"
              />
              <button
                type="button"
                className="modal-close"
                onClick={() => setSearchOpen(false)}
                aria-label="Close search"
              >
                <Icon name="close" />
              </button>
            </div>
            <div className="search-results">
              {searchQuery.trim() ? (
                searchResults.map((r: any, i: number) => (
                  <button
                    type="button"
                    className="search-result"
                    key={`${r.kind}-${r.title}-${i}`}
                    onClick={() => openSearchResult(r)}
                  >
                    <span>
                      <Icon name={r.icon} />
                    </span>
                    <span className="search-result-copy">
                      <b>{r.title}</b>
                      <small>{r.subtitle}</small>
                    </span>
                    <Icon name="arrow" size={14} />
                  </button>
                ))
              ) : (
                <EmptyState
                  icon="search"
                  title="Search the complete Proctor Studio"
                  text="Find any sidebar destination, assigned assessment, candidate, incident or evidence record."
                />
              )}
              {searchQuery.trim() && !searchResults.length && (
                <EmptyState
                  icon="search"
                  title="No matching records"
                  text="Try a candidate number, session code, incident ID, evidence ID or workspace title."
                />
              )}
            </div>
          </section>
        </div>
      )}
      <AppModal modal={modal} close={closeModal} cb={cb} prefs={prefs} setPrefs={setPrefs} />
      <div className="toast-stack" aria-live="polite">
        {toasts.map(t => (
          <article className={`toast ${t.tone || "info"}`} key={t.id}>
            <span>
              <Icon
                name={
                  t.tone === "good" ? "check" : t.tone === "warn" || t.tone === "danger" ? "alert" : "shield"
                }
                size={16}
              />
            </span>
            <span className="toast-copy">
              <b>{t.title}</b>
              <span>{t.message}</span>
            </span>
            <button
              type="button"
              onClick={() => setToasts(v => v.filter(x => x.id !== t.id))}
              aria-label="Dismiss notification"
            >
              <Icon name="close" size={13} />
            </button>
          </article>
        ))}
      </div>
      <style>{CSS + dropdownThemeStyles}</style>
    </div>
  );
}

export default App;
